import { useState, useRef, useEffect } from 'react'
import { Collapse } from './UI'

// ── New interaction primitives (design sync "Interaction proposals", 2026-08-14) ──
// Each replaces one existing hand-rolled/plain pattern app-wide. Kept out of UI.jsx
// (which already carries FilmstripTabs/MagneticToggle, the two closest siblings of
// the existing Tabs/SegmentedControl ladder) because these five are more novel
// shapes, not drop-in variants of an existing primitive's exact API.

// ── DossierAccordion ──────────────────────────────────────────────────────────
// Replaces multi-item settings-style accordions (previously ad-hoc .collapse-grid
// stacks). A gold spine connects the sections; each numeral flips from a serif
// index to a checkmark when its section is open — the app's archival Fraunces
// voice applied to a control, not just a title.
// `items`: [{ key, title, body (node) }]. `openKeys`/`onToggle` is a CONTROLLED
// multi-open set (a Set of keys) so callers can allow multiple sections open at
// once (matches how Settings' groups behave today) — pass a Set with 0/1 members
// if only one should ever be open.
export function DossierAccordion({ items, openKeys, onToggle, style }) {
  return (
    <div style={{ position: 'relative', paddingLeft: 26, ...style }}>
      <div aria-hidden style={{ position: 'absolute', left: 9, top: 6, bottom: 6, width: 1, background: 'rgba(212,175,55,0.28)' }} />
      {items.map((it, i) => {
        const open = openKeys.has(it.key)
        return (
          <div key={it.key} style={{ marginBottom: 10, position: 'relative' }}>
            <span aria-hidden style={{
              position: 'absolute', left: -26, top: 2, width: 20, height: 20, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: open ? 'var(--font)' : 'var(--font-serif)', fontSize: open ? 11 : 10, fontWeight: 600,
              color: open ? 'var(--amber-ink)' : 'var(--amber)',
              background: open ? 'var(--grad-amber)' : 'rgba(212,175,55,0.12)',
              border: open ? 'none' : '1px solid rgba(212,175,55,0.4)',
              transition: 'all 280ms var(--ease-pop)',
            }}>{open ? '✓' : String(i + 1).padStart(2, '0')}</span>
            <button onClick={() => onToggle(it.key)} aria-expanded={open} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderLeft: `2px solid ${open ? 'var(--amber)' : 'transparent'}`,
              borderRadius: 'var(--radius-md)', padding: '13px 14px', cursor: 'pointer',
              color: 'var(--text-strong)', fontFamily: 'var(--font)', fontWeight: 700, fontSize: 13.5, textAlign: 'left',
              transition: 'border-color 240ms ease',
            }}>
              <span>{it.title}</span>
              <span aria-hidden style={{
                width: 8, height: 8, borderRight: '2px solid var(--text-muted)', borderBottom: '2px solid var(--text-muted)',
                transform: open ? 'rotate(-135deg)' : 'rotate(45deg)', transition: 'transform 300ms var(--ease-pop)',
              }} />
            </button>
            <Collapse open={open}>
              <div style={{ padding: '10px 4px 4px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-dim)' }}>{it.body}</div>
            </Collapse>
          </div>
        )
      })}
    </div>
  )
}

// ── QuickPickMenu ──────────────────────────────────────────────────────────────
// Replaces Dropdown/MenuRow for an action menu that has a few HIGH-frequency
// actions worth their own icon-only row (e.g. mark watched, add, share) above the
// usual text list. Opens with a spring pop from the trigger's corner; rows cascade
// in with a stagger. Uncontrolled open state — pass `trigger` a render prop
// receiving {onClick, open} so any element can be the anchor.
// `quickActions`: [{ key, glyph, onClick }] (icon-only chips).
// `rows`: [{ key, label, onClick, danger? }] (the text list).
// `header`: optional node (matches Dropdown's header slot, e.g. a title + hint line).
export function QuickPickMenu({ trigger, quickActions = [], rows = [], header, align = 'start', style }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const close = () => setOpen(false)
  // Escape closes the menu, matching Dropdown's keyboard behavior — the outside-click
  // overlay alone left keyboard users with no way to dismiss (audit 2026-08-14).
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])
  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block', ...style }}>
      {trigger({ onClick: () => setOpen(o => !o), open })}
      {open && (
        <>
          <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-dropdown)' }} />
          <div role="menu" style={{
            position: 'absolute', top: 'calc(100% + 10px)', [align === 'end' ? 'right' : 'left']: 0,
            transformOrigin: `top ${align === 'end' ? 'right' : 'left'}`, width: 236,
            background: 'var(--sheet)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 10,
            boxShadow: 'var(--shadow-xl)', '--nxa': 'quickPickPop 0.32s var(--ease-pop) both',
            zIndex: 'calc(var(--z-dropdown) + 1)',
          }}>
            {header && (
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:8,
                padding:'2px 8px 8px', fontSize:10, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase',
                color:'var(--text-dim)' }}>{header}</div>
            )}
            {quickActions.length > 0 && (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, padding: '0 2px' }}>
                  {quickActions.map((q, i) => (
                    <button key={q.key} onClick={() => { q.onClick?.(); close() }} aria-label={q.label} style={{
                      width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(var(--amber-rgb),0.10)', border: '1px solid rgba(var(--amber-rgb),0.28)',
                      color: 'var(--amber)', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)',
                      '--nxa': 'quickPickRowIn 260ms ease both', animationDelay: `${i * 40}ms`,
                    }}>{q.glyph}</button>
                  ))}
                </div>
                <div style={{ height: 1, background: 'var(--border)', margin: '0 0 8px' }} />
              </>
            )}
            {rows.map((m, i) => m.divider ? (
              <div key={m.key || `div-${i}`} style={{ height: 1, background: 'var(--border-soft)', margin: '6px 4px' }} />
            ) : (
              <button key={m.key} onClick={() => { m.onClick?.(); if (!m.keepOpen) close() }} style={{
                display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                background: m.active ? 'rgba(var(--amber-rgb),0.1)' : 'none', border: 'none',
                padding: '9px 10px', borderRadius: 9, cursor: 'pointer',
                color: m.danger ? 'var(--state-bad)' : 'var(--text)',
                fontFamily: 'var(--font)', fontSize: 13.5, fontWeight: 500,
                '--nxa': 'quickPickRowIn 260ms ease both', animationDelay: `${(quickActions.length + i) * 40}ms`,
              }}>
                {m.icon != null && <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{m.icon}</span>}
                <span style={{ flex: 1 }}>{m.label}</span>
                {m.trailing}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── RippleChip ─────────────────────────────────────────────────────────────────
// Replaces CircleFilter's plain-toggle circles: a gold ring bursts outward from a
// circle the instant it's picked, then fades — a quick physical acknowledgement
// instead of a silent color swap. `selected` drives the fill; `onSelect` fires on
// tap. The burst is purely presentational (its own local one-shot state), so this
// is a safe drop-in wherever a circular single-select chip is rendered.
export function RippleChip({ selected, onSelect, children, size = 52, style }) {
  const [burst, setBurst] = useState(false)
  return (
    <button onClick={() => { setBurst(true); setTimeout(() => setBurst(false), 500); onSelect?.() }}
      aria-pressed={selected} style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 0, ...style }}>
      {burst && (
        <span aria-hidden style={{
          position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid var(--amber)',
          '--nxa': 'rippleChipBurst 500ms ease-out forwards',
        }} />
      )}
      <span style={{
        width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font)', fontSize: 15, fontWeight: 700,
        background: selected ? 'var(--grad-amber)' : 'var(--bg-card)',
        color: selected ? 'var(--amber-ink)' : 'var(--text-muted)',
        border: selected ? 'none' : '1px solid var(--border)',
        transition: 'all 220ms ease',
      }}>{children}</span>
    </button>
  )
}

// ── RailTabs ───────────────────────────────────────────────────────────────────
// Replaces dense horizontal admin tab rows with a vertical rail + a sliding
// side-indicator — room for many sections without horizontal scroll or overflow
// chevrons. Same tabs/value/onChange shape as Tabs/PillTabs.
export function RailTabs({ tabs, value, onChange, itemHeight = 44, style }) {
  const opts = tabs.map(t => (typeof t === 'string' ? { value: t, label: t } : t))
  const idx = Math.max(0, opts.findIndex(o => o.value === value))
  return (
    <div role="tablist" style={{
      position: 'relative', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius-md)', padding: 6, ...style,
    }}>
      <span aria-hidden style={{
        position: 'absolute', left: 6, right: 6, height: itemHeight - 4,
        top: `calc(6px + ${idx} * ${itemHeight}px)`,
        borderRadius: 'var(--radius-sm)', background: 'var(--grad-amber)',
        boxShadow: '0 6px 16px -8px rgba(var(--amber-rgb),0.8)',
        transition: 'top 340ms cubic-bezier(0.22,1,0.36,1)',
      }} />
      {opts.map(o => {
        const on = o.value === value
        return (
          <button key={o.value} role="tab" aria-selected={on} onClick={() => onChange?.(o.value)} style={{
            position: 'relative', zIndex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
            height: itemHeight, padding: '0 14px', fontFamily: 'var(--font)', fontSize: 13.5,
            fontWeight: on ? 800 : 600, color: on ? 'var(--amber-ink)' : 'var(--text-dim)',
            transition: 'color 260ms ease',
          }}>{o.label}</button>
        )
      })}
    </div>
  )
}

// ── FlipChip ───────────────────────────────────────────────────────────────────
// Replaces plain rarity/set filter chips: a quick card-flip (rotateY) plays before
// the chip lands on its selected look — a wink at the trading-card theme, for use
// anywhere a "flip to reveal the pick" reads as on-brand (Cards/CardBinder rarity
// filters). Same controlled shape as a plain button: `selected` + `onSelect`.
export function FlipChip({ selected, onSelect, children, accent = 'var(--purple)', style }) {
  const [flipping, setFlipping] = useState(false)
  return (
    <button onClick={() => { setFlipping(true); setTimeout(() => setFlipping(false), 260); onSelect?.() }}
      aria-pressed={selected} style={{
        padding: '9px 15px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontFamily: 'var(--font)',
        fontSize: 12.5, fontWeight: 700,
        background: selected ? accent : 'var(--bg-card)',
        border: `1px solid ${selected ? 'transparent' : 'var(--border)'}`,
        color: selected ? 'var(--amber-ink)' : 'var(--text-dim)',
        '--nxa': flipping ? 'flipChipFlip 260ms ease' : 'none',
        transition: 'background 200ms ease, color 200ms ease', ...style,
      }}>{children}</button>
  )
}
